
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useUserName } from '@/hooks/useUserName';
import { useOrganizationData } from '@/hooks/useOrganizationData';
import DashboardLayout from '@/components/dashboard/layout/DashboardLayout';
import DashboardRoutes from '@/components/dashboard/DashboardRoutes';

const Dashboard = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [activePage, setActivePage] = useState('profiles');
  const userName = useUserName();
  const { daysRemaining, isTrialActive } = useOrganizationData();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, navigate, loading]);

  // This is already correct because signOut from useAuth returns a Promise
  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      console.log("Traitement de déconnexion initié...");
      const result = await signOut();
      
      if (result.error) {
        throw result.error;
      }
      
      console.log("Déconnexion réussie, redirection...");
      
      toast({
        title: "Déconnexion réussie",
        description: "Vous avez été déconnecté avec succès.",
        variant: "default",
      });
      
      window.location.href = '/login';
    } catch (error: any) {
      console.error("Erreur lors de la déconnexion:", error);
      toast({
        title: "Erreur de déconnexion",
        description: "Une erreur est survenue lors de la déconnexion. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteUser = () => {
    navigate('/invitation');
  };

  const handlePageChange = (pageId: string) => {
    setActivePage(pageId);
    if (pageId === 'settings') {
      navigate('/dashboard/settings');
    } else if (pageId === 'plans') {
      navigate('/plans');
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;
  }

  if (!loading && !user) {
    return null;
  }

  return (
    <DashboardLayout
      userName={userName}
      activePage={activePage}
      setActivePage={handlePageChange}
      handleSignOut={handleSignOut}
      handleInviteUser={handleInviteUser}
      isTrialActive={isTrialActive}
      daysRemaining={daysRemaining}
    >
      <DashboardRoutes 
        activePage={activePage} 
        handlePageChange={handlePageChange} 
      />
    </DashboardLayout>
  );
};

export default Dashboard;
